import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { DriveEntry } from '@/lib/drive/driveClient';
import { driveClient } from '@/lib/drive/driveClient';
import { entryIconKind, formatBytes } from '@/lib/drive/format';

interface DrivePreviewModalProps {
  entry: DriveEntry | null;
  onClose: () => void;
  onDownload: (entry: DriveEntry) => void;
}

type ViewerKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'code' | 'unsupported';

function viewerKindOf(entry: DriveEntry): ViewerKind {
  const kind = entryIconKind(entry.name, entry.mimeType);
  if (kind === 'image' || kind === 'video' || kind === 'audio' || kind === 'pdf' || kind === 'text' || kind === 'code') {
    return kind as ViewerKind;
  }
  return 'unsupported';
}

/**
 * The drive's file viewer: images, video, audio, PDF and text/code render in place;
 * anything else answers with the type icon and the download door. Bytes ride the
 * preview endpoint as a blob, so the bearer never has to appear in a URL.
 */
export function DrivePreviewModal({ entry, onClose, onDownload }: DrivePreviewModalProps) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const kind = entry ? viewerKindOf(entry) : 'unsupported';
  const isTextual = kind === 'text' || kind === 'code';

  useEffect(() => {
    if (!entry) return;
    let revoked: string | null = null;
    setBlobUrl(null);
    setTextContent(null);
    setError(null);
    (async () => {
      try {
        const blob = await driveClient.readPreviewBlobById(entry.id);
        if (isTextual) {
          setTextContent(await blob.text());
          return;
        }
        const url = URL.createObjectURL(blob);
        revoked = url;
        setBlobUrl(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [entry, isTextual]);

  const body = useMemo(() => {
    if (!entry) return null;
    if (error) {
      return <p className="px-6 py-10 text-center text-sm text-destructive">{error}</p>;
    }
    if (kind === 'unsupported') {
      return (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <DocumentIcon className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('drive.previewUnsupported')}</p>
          <button
            type="button"
            onClick={() => onDownload(entry)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {t('drive.download')}
          </button>
        </div>
      );
    }
    if (isTextual) {
      if (textContent === null) return <Spinner />;
      return (
        <pre className="max-h-[70vh] overflow-auto rounded-lg bg-muted p-4 text-left text-xs leading-relaxed text-foreground">
          {textContent}
        </pre>
      );
    }
    if (!blobUrl) return <Spinner />;
    if (kind === 'image') {
      return <img src={blobUrl} alt={entry.name} className="max-h-[70vh] max-w-full rounded-lg object-contain" />;
    }
    if (kind === 'video') {
      return <video src={blobUrl} controls className="max-h-[70vh] w-full rounded-lg" />;
    }
    if (kind === 'audio') {
      return <audio src={blobUrl} controls className="w-full" />;
    }
    return <iframe src={blobUrl} title={entry.name} className="h-[70vh] w-full rounded-lg border border-border-color bg-white" />;
  }, [blobUrl, entry, error, isTextual, kind, onDownload, textContent, t]);

  return (
    <Transition show={!!entry} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 overflow-hidden" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="absolute inset-0 bg-overlay" />
        </Transition.Child>
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className={clsx(
                'flex w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border-color bg-card shadow-xl',
                kind === 'unsupported' || error ? 'max-w-lg' : 'max-w-4xl',
              )}
            >
              <div className="flex flex-nowrap items-center justify-between gap-3 border-b border-border-color px-4 py-3">
                <div className="min-w-0">
                  <Dialog.Title className="truncate whitespace-nowrap text-sm font-medium text-foreground" title={entry?.name}>
                    {entry?.name}
                  </Dialog.Title>
                  <p className="whitespace-nowrap text-xs text-muted-foreground">
                    {entry ? formatBytes(entry.size) : ''}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  {entry && kind !== 'unsupported' && (
                    <button
                      type="button"
                      title={t('drive.download')}
                      onClick={() => onDownload(entry)}
                      className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    title={t('common.close')}
                    onClick={onClose}
                    className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex min-h-40 items-center justify-center overflow-auto p-4">{body}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function Spinner() {
  return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-color border-t-accent" />;
}
